import React, { useState, useEffect } from 'react';
import { Asset, getUserAssets } from '../services/assets';
import Portal from './Portal';

interface AssetPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  selectedAssetId?: string;
}

export default function AssetPickerModal({
  isOpen,
  onClose,
  onSelect,
  selectedAssetId,
}: AssetPickerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's assets when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      getUserAssets('image')
        .then((userAssets) => {
          setAssets(userAssets);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load assets:', err);
          setError(err instanceof Error ? err.message : 'Failed to load assets');
          setLoading(false);
        });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredAssets = assets.filter((asset) => {
    const searchLower = searchQuery.toLowerCase();
    const promptMatch = asset.prompt?.toLowerCase().includes(searchLower);
    const operationMatch = asset.operation_type?.toLowerCase().includes(searchLower);
    return promptMatch || operationMatch;
  });

  const handleSelect = (asset: Asset) => {
    onSelect(asset);
    onClose();
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-bg border border-white/20 rounded-xl p-6 w-full max-w-[min(900px,calc(100vw-2rem))] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Select from Assets</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search assets by prompt or operation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
          />
        </div>

        {loading && (
          <div className="text-center py-8 text-white/60">Loading assets...</div>
        )}

        {error && (
          <div className="text-center py-8 text-red-500">Error: {error}</div>
        )}

        {!loading && !error && filteredAssets.length === 0 && (
          <div className="text-center py-8 text-white/60">
            {searchQuery ? 'No assets found matching your search.' : 'No assets available. Upload an image first!'}
          </div>
        )}

        {!loading && !error && (
          <div className="auto-grid">
            {filteredAssets.map((asset) => {
              const imageUrl = asset.s3_url || asset.storage_url || '';
              const displayName = asset.prompt 
                ? (asset.prompt.length > 30 ? asset.prompt.substring(0, 30) + '...' : asset.prompt)
                : asset.operation_type || 'Asset';
              
              return (
                <button
                  key={asset.id}
                  onClick={() => handleSelect(asset)}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    selectedAssetId === asset.id
                      ? 'border-white bg-white/10'
                      : 'border-white/20 bg-white/5 hover:border-white/40'
                  }`}
                >
                  <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-white/10">
                    <img
                      src={imageUrl}
                      alt={displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder if image doesn't exist
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EAsset%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  <p className="text-white text-sm font-medium text-center">{displayName}</p>
                  {asset.status && (
                    <p className="text-white/50 text-xs text-center mt-1">
                      {asset.status}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </Portal>
  );
}

